/**
 * Notification UX constants (Phase 1 realtime).
 *
 * Constants only — no runtime reads. Protocol-level numbers (dedupe window,
 * poll fallback, back-off) are owned by `contracts/realtime-policy.ts` and
 * re-exported here so the app has one import for "notification settings" but
 * each number still has exactly one definition.
 */
import { DEDUPE_WINDOW_SIZE, POLL_FALLBACK_SECONDS } from '@/contracts/realtime-policy';

/** How long a realtime notification toast stays on screen. */
export const NOTIFICATION_TOAST_DURATION_MS = 4_000;

/** Number of recent `event_id`s remembered for de-duplication (spec: 500). */
export const NOTIFICATION_DEDUPE_SIZE = DEDUPE_WINDOW_SIZE;

/** Poll cadence for the inbox/badge while the socket is not connected. */
export const NOTIFICATION_POLL_FALLBACK_MS = POLL_FALLBACK_SECONDS * 1_000;

/**
 * Icon shown on host (OS) notifications. A PWA asset that ships with every
 * build, resolved against the app origin — the same 192px mark the manifest
 * uses, so the notification matches the installed app icon.
 */
export const HOST_NOTIFICATION_ICON = '/assets/icons/icon-192.png';

/**
 * `localStorage` key remembering that the user dismissed the in-app rationale
 * for device notifications. Versioned so a redesign of the prompt can re-ask.
 */
export const DEVICE_NOTIFICATION_PROMPT_DISMISSED_KEY = 'syntry.device_notification_prompt_dismissed_v1';

/**
 * The three clips Syntry can play, and what each one means.
 *
 * - `notification` — something ARRIVED. Rides every realtime toast.
 * - `success` — something the user just did WORKED.
 * - `error` — something the user just did FAILED (also used for `warning`).
 *
 * All three are ERPat's own sounds, taken verbatim from its
 * `public/assets/sounds/` and re-encoded to one profile (mono, 44.1 kHz,
 * ~64 kbps, metadata stripped) so the set is ~31 KB rather than ~168 KB —
 * `notification` is the chime ERPat's web app plays, `success`/`error` are the
 * pair its Kiosk plays on a clock-in accepted / rejected.
 *
 * PWA assets resolved against the app origin, like {@link HOST_NOTIFICATION_ICON},
 * and precached (`mp3` is in vite.config.ts's `globPatterns`) so the first play
 * never waits on the network — a cold fetch at `play()` time is either a delay
 * or silence.
 */
export type AppSoundKind = 'notification' | 'success' | 'error';

export const APP_SOUND_SRC: Record<AppSoundKind, string> = {
  notification: '/assets/sounds/notification.mp3',
  success: '/assets/sounds/success.mp3',
  error: '/assets/sounds/error.mp3',
};

/**
 * Fixed playback volume. ERPat has a per-user `notification_sound_volume`
 * setting; Syntry has no equivalent preference surface, so this is the one
 * value — low enough to be a cue rather than an interruption.
 */
export const NOTIFICATION_SOUND_VOLUME = 0.5;

/**
 * Floor between two plays OF THE SAME CLIP. A notification burst and a message
 * burst can flush in the same tick (each type has its own `DISPATCH_COALESCE_MS`
 * window); the user should hear one sound, not two.
 *
 * Per-clip rather than global on purpose: a realtime chime must not swallow the
 * success sound of a save the user makes a moment later — those are two
 * different events and deserve two different cues.
 */
export const NOTIFICATION_SOUND_MIN_INTERVAL_MS = 1_500;
